import { EXTERIOR_TREATMENT_MATERIAL_KEY } from "../domain/finishes/calculateExteriorTreatment";
import type {
	EngineAuditMetadata,
	EngineEvidenceRowConfig,
	EngineMaterialClassification,
	EngineMaterialDescriptor,
	EngineVisualMapping,
} from "./auditTypes";
import type {
	BuildInput,
	CalculationResult,
	RawCalculationResult,
} from "./types";

const BARREL_VISUAL_MAPPINGS: EngineVisualMapping[] = [
	{
		prefix: "detail/foundation/beam-",
		category: "foundation.beams",
		status: "visual-backed",
	},
	{
		prefix: "detail/foundation/plinth-",
		category: "foundation.plinths",
		status: "visual-backed",
	},
	{
		prefix: "detail/foundation/shoe-",
		category: "foundation.shoes",
		status: "visual-backed",
	},
	{ prefix: "detail/entry/", category: "entry.steps", status: "visual-backed" },
	{
		prefix: "detail/barrel-shell/",
		category: "barrel.staves",
		status: "visual-backed",
	},
	{
		prefix: "detail/barrel-end-cap/",
		category: "barrel.end-caps",
		status: "visual-backed",
	},
	{
		prefix: "detail/barrel-band/",
		category: "barrel.bands",
		status: "visual-backed",
	},
	{
		prefix: "detail/barrel-cradle/",
		category: "barrel.cradles",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/barrel-",
		category: "barrel.end-rings",
		status: "visual-backed",
	},
	{
		prefix: "detail/bench-",
		category: "interior.bench",
		status: "visual-backed",
	},
	{
		prefix: "detail/floor-panel/",
		category: "structure.floor-surface",
		status: "visual-backed",
	},
	{
		prefix: "detail/opening-trim/",
		category: "openings.trim",
		status: "visual-backed",
	},
	{
		prefix: "detail/procedural-door",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/heater/",
		category: "systems.heater",
		status: "visual-backed",
	},
	{
		prefix: "detail/shield/",
		category: "systems.heat-shield",
		status: "visual-backed",
	},
	{
		prefix: "detail/chimney/",
		category: "systems.chimney",
		status: "visual-backed",
	},
	{
		prefix: "detail/vent/",
		category: "systems.ventilation",
		status: "visual-backed",
	},
];

function classifyBarrelMaterial(
	material: EngineMaterialDescriptor,
): EngineMaterialClassification {
	const label = material.material.toLowerCase();

	// Facade colour (issue 134): by stable key, not by label substring. It
	// changes how the cladding LOOKS but adds no mesh of its own, so it is a
	// consumable — and it has to be classified, because an unmatched label
	// falls through to "uncategorized", which the BOM↔3D parity gate reads as
	// a silent claim. Keying off the name would make that gate fail on a copy
	// edit to the row's Swedish parenthetical.
	if (material.materialKey === EXTERIOR_TREATMENT_MATERIAL_KEY) {
		return { category: "bundles.consumables", status: "derived-nonvisual" };
	}

	if (label.includes("aggregat") || label.includes("ventiler")) {
		return { category: "bundles.systems", status: "manual-placeholder" };
	}
	if (label.includes("stavvirke"))
		return { category: "barrel.staves", status: "visual-backed" };
	if (label.includes("gavlar"))
		return { category: "barrel.end-caps", status: "visual-backed" };
	if (label.includes("stålband"))
		return { category: "barrel.bands", status: "visual-backed" };
	if (label.includes("vaggor"))
		return { category: "barrel.cradles", status: "visual-backed" };
	if (label.includes("lav"))
		return { category: "interior.bench", status: "visual-backed" };
	if (label.includes("golv"))
		return { category: "structure.floor-surface", status: "visual-backed" };
	if (label.includes("plint") || label.includes("betong"))
		return { category: "foundation.plinths", status: "visual-backed" };
	if (label.includes("bärlina"))
		return { category: "foundation.beams", status: "visual-backed" };

	// Drawn, but previously fell through to `uncategorized` — see the rectangular
	// classifier for the same fix and the reasoning behind it.
	if (label.includes("bastudörr") || label.includes("ytterdörr")) {
		return { category: "openings.doors", status: "visual-backed" };
	}
	if (label.includes("foder"))
		return { category: "openings.trim", status: "visual-backed" };
	if (label.startsWith("fönster"))
		return { category: "openings.glass", status: "visual-backed" };
	if (label.includes("stolpsko"))
		return { category: "foundation.hardware", status: "visual-backed" };
	if (label.includes("bastuskorsten") || label.includes("sauna chimney")) {
		return { category: "systems.chimney", status: "visual-backed" };
	}
	if (label.startsWith("strålskydd")) {
		// detail/shield/*
		return { category: "systems.heat-shield", status: "visual-backed" };
	}
	if (label.includes("styrpanel"))
		return { category: "systems.heater", status: "visual-backed" };
	// Structural blocking that the BOM buys but nothing draws — real lumber, so it
	// must not be filed under consumables. Flagged as a modelling gap, not a
	// classification one: see the BOM/geometry parity note.
	if (label.includes("kortling")) {
		return { category: "framing.blocking", status: "derived-nonvisual" };
	}
	// Genuinely non-geometric: consumables and site work.
	if (
		label.includes("markduk") ||
		label.includes("dräneringsgrus") ||
		label.includes("makadam") ||
		label.includes("alutejp") ||
		label.includes("fogtejp") ||
		label.includes("skruv") ||
		label.includes("beslagspaket") ||
		label.includes("ytbehandling") ||
		label.includes("brandtätning") ||
		label.includes("sotarlucka")
	) {
		return { category: "bundles.consumables", status: "derived-nonvisual" };
	}

	return { category: "uncategorized", status: "derived-nonvisual" };
}

export function getBarrelAuditMetadata(): EngineAuditMetadata {
	return {
		visualMappings: BARREL_VISUAL_MAPPINGS,
		classifyMaterial: classifyBarrelMaterial,
	};
}

export function getBarrelEvidenceConfig(
	input: BuildInput,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_calculation?: CalculationResult | RawCalculationResult,
): EngineEvidenceRowConfig[] {
	const W = input.dimensions.width;
	const L = input.dimensions.length;
	const R = W / 2;
	const circumference = Math.PI * W;
	const numStaves = Math.ceil(circumference / 0.09);
	const numBands = Math.max(2, Math.floor(L / 0.8));
	const numCradles = L <= 2.5 ? 2 : 3;
	const floorArea = W * 0.7 * L;
	const endCapArea = Math.PI * R * R;

	return [
		{
			id: "barrel-staves",
			category: "barrel.staves",
			sourceLabel: "Stavvirke (Värmebehandlat)",
			sourceType: "material",
			dimension: "38×90 mm",
			unit: "lpm",
			rawTotal: Math.ceil(numStaves * L),
			status: "visual-backed",
			formula: "ceil(numStaves * length)",
			variables: [
				{ label: "numStaves", value: numStaves.toString() },
				{ label: "circumference", value: circumference.toFixed(2) },
			],
			notes: ["Cylinderns mantelyta uttryckt som stavvirke."],
		},
		{
			id: "barrel-end-caps",
			category: "barrel.end-caps",
			sourceLabel: "Gavlar (Limträlaminat)",
			sourceType: "material",
			dimension: `Diameter ${Math.round(W * 1000)}mm`,
			unit: "st",
			rawTotal: 2,
			status: "visual-backed",
			formula: "2 gavlar",
			variables: [{ label: "endCapArea", value: endCapArea.toFixed(2) }],
			notes: ["Fram- och bakkap i tunnan."],
		},
		{
			id: "barrel-bands",
			category: "barrel.bands",
			sourceLabel: "Stålband",
			sourceType: "material",
			dimension: "30×3 mm",
			unit: "st",
			rawTotal: numBands,
			status: "visual-backed",
			formula: "max(2, floor(length / 0.8))",
			variables: [{ label: "length", value: L.toFixed(2) }],
			notes: ["Band som håller stavkroppen samman."],
		},
		{
			id: "barrel-cradles",
			category: "barrel.cradles",
			sourceLabel: "Vaggor / Stöd",
			sourceType: "material",
			dimension: "100×200 mm K-virke",
			unit: "st",
			rawTotal: numCradles,
			status: "visual-backed",
			formula: "length <= 2.5 ? 2 : 3",
			variables: [{ label: "length", value: L.toFixed(2) }],
			notes: ["Bärvaggor under tunnan."],
		},
		{
			id: "barrel-floor",
			category: "structure.floor-surface",
			sourceLabel: "Golvbrädor (inre)",
			sourceType: "material",
			dimension: "28×120 mm",
			unit: "m²",
			rawTotal: Math.ceil(floorArea),
			status: "visual-backed",
			formula: "ceil(width * 0.7 * length)",
			variables: [{ label: "floorArea", value: floorArea.toFixed(2) }],
			notes: ["Invändigt plan golv i tunnan."],
		},
	];
}
